import React, { useState, useEffect } from 'react';
import BusinessDetails from './BusinessDetails';
import Representatives from './Representatives';
import BankAccount from './BankAccount';
import { Check } from 'lucide-react';
import { useParams } from 'react-router-dom';
import useAxios from '../hooks/useAxios';
import { config } from '../config';
import ErrorDisplay from '../components/ErrorDisplay';
import Header from '../components/Header';

const OnboardingFormPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const axios = useAxios();
    const [profile, setProfile] = useState<any>({
        businessDetails: {},
        representatives: [],
        bankAccount: {}
    });
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dataLoaded, setDataLoaded] = useState(false);

    useEffect(() => {
        const fetchOnboardingData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${config.backendUrl}/accounts/onboarding-forms/${id}`);
                
                if (response.data) {
                    setProfile({
                        businessDetails: response.data.businessDetails || {},
                        representatives: response.data.representatives || [],
                        bankAccount: response.data.bankAccount || {}
                    });
                    setDataLoaded(true);
                } else {
                    setError('Onboarding form not found');
                }
            } catch (err: any) {
                console.error('Failed to fetch onboarding data:', err);
                if (err.response?.status === 404) {
                    setError('Onboarding form not found');
                } else {
                    setError(err.response?.data?.message || 'Failed to load onboarding data');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchOnboardingData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (loading) {
        return (
            <div className="h-full flex flex-col bg-gray-50">
                <Header />
                <main className="flex-1 flex items-center justify-center">
                    <div className="text-gray-600">Loading onboarding data...</div>
                </main>
            </div>
        );
    }

    if (error || !dataLoaded) {
        return <ErrorDisplay error={error || 'Onboarding form not found'} />;
    }

    return (
        <div className="h-full flex flex-col bg-gray-50">
            <Header />

            {/* Main Content */}
            <main className="min-h-0 flex flex-col flex-grow items-center overflow-y-auto py-10">
                <div className="container w-full bg-white rounded-xl shadow-sm p-8">
                    <div className="mb-8">
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            Business Onboarding
                        </h1>
                        <p className="text-gray-600">
                            Please provide your business information to complete the onboarding process.
                        </p>
                    </div>

                    <div className='flex flex-col-reverse md:grid md:grid-cols-5 md:gap-10'>
                        <div className='md:col-span-3'>
                            {step === 1 && (
                                <BusinessDetails profile={profile} setStep={setStep} setProfile={setProfile} />
                            )}
                            {step === 2 && (
                                <Representatives profile={profile} setStep={setStep} setProfile={setProfile} />
                            )}
                            {step === 3 && (
                                <BankAccount profile={profile} setStep={setStep} setProfile={setProfile} />
                            )}
                        </div>
                        <div className="md:col-span-2 md:pl-10 md:pt-10">
                            <div className="flex flex-col space-y-4 mb-4">
                                <div 
                                    onClick={() => setStep(1)} 
                                    className={`cursor-pointer flex items-center p-3 rounded-lg transition-all ${
                                        step === 1 
                                            ? 'bg-blue-50 border-2 border-blue-500 text-blue-700' 
                                            : 'bg-gray-50 border-2 border-transparent text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                                        step === 1 
                                            ? 'bg-blue-500 text-white' 
                                            : step > 1 
                                                ? 'bg-green-500 text-white' 
                                                : 'bg-gray-300 text-gray-600'
                                    }`}>
                                        {step > 1 ? (
                                            <Check className="w-5 h-5" />
                                        ) : (
                                            <span className="text-sm font-semibold">1</span>
                                        )}
                                    </div>
                                    <span className="font-medium">Business Details</span>
                                </div>
                                <div 
                                    onClick={() => setStep(2)} 
                                    className={`cursor-pointer flex items-center p-3 rounded-lg transition-all ${
                                        step === 2 
                                            ? 'bg-blue-50 border-2 border-blue-500 text-blue-700' 
                                            : 'bg-gray-50 border-2 border-transparent text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                                        step === 2 
                                            ? 'bg-blue-500 text-white' 
                                            : step > 2 
                                                ? 'bg-green-500 text-white' 
                                                : 'bg-gray-300 text-gray-600'
                                    }`}>
                                        {step > 2 ? (
                                            <Check className="w-5 h-5" />
                                        ) : (
                                            <span className="text-sm font-semibold">2</span>
                                        )}
                                    </div>
                                    <span className="font-medium">Representatives</span>
                                </div>
                                <div 
                                    onClick={() => setStep(3)} 
                                    className={`cursor-pointer flex items-center p-3 rounded-lg transition-all ${
                                        step === 3 
                                            ? 'bg-blue-50 border-2 border-blue-500 text-blue-700' 
                                            : 'bg-gray-50 border-2 border-transparent text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                                        step === 3 
                                            ? 'bg-blue-500 text-white' 
                                            : step > 3 
                                                ? 'bg-green-500 text-white' 
                                                : 'bg-gray-300 text-gray-600'
                                    }`}>
                                        {step > 3 ? (
                                            <Check className="w-5 h-5" />
                                        ) : (
                                            <span className="text-sm font-semibold">3</span>
                                        )}
                                    </div>
                                    <span className="font-medium">Bank Account</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default OnboardingFormPage;

